import { Drawer, message } from 'antd';
//@ts-ignore
import Cookies from 'js-cookie';

import type { ColumnsVisibility, Template, TemplateParam } from '@/components/WellLoggingOperations/types';

import { ActivationButton } from './ActivationButton';
import { DropdownMenu } from './DropdownMenu';
import { Columns } from './Columns';

import { useTemplatesId, useActiveTemplateDispatch } from '../../context/TemplatesProvider';
import { useDeleteTemplate } from '../../hooks/useDeleteTemplate';
import { useUpdateTemplate } from '../../hooks/useUpdateTemplate';
import { useSelectedWells } from '../../hooks/useSelectedWells';
import { paramColumnToColumnKey } from '@/store/well-logs/tabletSettings/config';

interface InfoContentProps {
    data: Template;
    onClose: () => void;
}

export const isValidJSON = (string: string) => {
    if (typeof string !== 'string') {
        return false;
    }

    try {
        JSON.parse(string);
        return true;
    } catch (e) {
        return false;
    }
};

const prepareParams = (params: TemplateParam[], columnsVisibility: ColumnsVisibility): TemplateParam[] => {
    if (columnsVisibility) {
        return (
            params
                .filter(({ paramColumn }) => columnsVisibility[paramColumnToColumnKey[paramColumn]])
                .map(param => {
                    if (param.paramColumn === 7) {
                        const newChildren =
                            param.children?.map(child => {
                                const str = isValidJSON(child.strParamValue)
                                    ? (JSON.parse(child.strParamValue).curveSettings[2] as string)
                                    : child.strParamValue;

                                return {
                                    ...child,
                                    strParamValue: str,
                                };
                            }) ?? [];
                        return {
                            ...param,
                            children: newChildren.filter((item, index) => {
                                return (
                                    newChildren.findIndex(child => child.strParamValue === item.strParamValue) === index
                                );
                            }),
                        };
                    }
                    return param;
                }) ?? []
        );
    }
    return params;
};

const InfoContent = (props: InfoContentProps) => {
    const { data, onClose } = props;

    const { id, templateTitle, username, params, columnsVisibility, is_default } = data;

    const { setActiveTemplateId, setDefaultTemplateId } = useActiveTemplateDispatch();
    const { activeTemplateId, defaultTemplateId } = useTemplatesId();

    const { isDeleting, deleteTemplate } = useDeleteTemplate();
    const { updateTemplate } = useUpdateTemplate();

    const selectedWells = useSelectedWells();

    const isActiveTemplate = activeTemplateId === id;
    const isDefaultTemplate = defaultTemplateId === id;

    const currentUsername = Cookies.get('username') as string;
    const isOwner = username === currentUsername;

    /*
        Вызов функции updateTemplate происходит только в том случае, если
        в выбранных скважинах имеется только одна скважина.
    */
    const isUpdateDisabled = selectedWells.length !== 1;

    const handleActivateClick = () => {
        setActiveTemplateId(id);
    };

    const handleDefaultClick = () => {
        setDefaultTemplateId(id);

        void message.info('Шаблон по умолчанию изменен');
    };

    const handleUpdateClick = () => {
        if (isUpdateDisabled) {
            void message.info('Выберите одну активную скважину для перезаписи шаблона');
            return;
        }

        if (isDefaultTemplate) {
            void message.info('Нельзя обновлять шаблон по умолчанию. Выберите другой шаблон.');
            return;
        }

        if (is_default) {
            void message.info('Нельзя обновлять стандартный шаблон. Выберите другой шаблон.');
            return;
        }

        const [well] = selectedWells;

        const data = {
            wellId: well.id,
            wellKey: well.key,
            templateId: id,
            templateTitle,
            username,
        };

        void updateTemplate(data, {
            onSuccess: () => {
                void message.success('Шаблон успешно обновлен');
                onClose();
            },
            onError: () => {
                void message.error('ERROR');
            },
        });
    };

    const handleDeleteClick = () => {
        if (isDefaultTemplate) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            void message.error('Нельзя удалить шаблон по умолчанию. Выберете другой шаблон перед удалением.');
            return;
        }

        if (is_default) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            void message.error('Нельзя удалить стандартный шаблон. Выберете другой шаблон перед удалением.');
            return;
        }

        void deleteTemplate(id, {
            onSuccess: () => {
                void message.success('Шаблон успешно удален');
                onClose();
            },
            onError: () => {
                void message.error('ERROR');
            },
        });
    };

    return (
        <div className="flex h-full flex-col bg-white dark:bg-block-secondary">
            <div className="p-4 text-xs">
                <div className="mb-3 text-black dark:text-white">{templateTitle}</div>
                <div className="text-gray-700 dark:text-font-secondary">
                    <i>Автор:</i> {username}
                </div>
            </div>

            <div className="flex items-center border-b border-t border-gray-500 p-4 dark:border-fill-primary">
                <ActivationButton
                    isActiveTemplate={isActiveTemplate}
                    disabled={isDeleting}
                    onClick={handleActivateClick}
                />
                <DropdownMenu
                    disabled={isDeleting}
                    isOwner={isOwner}
                    onDeleteTemplate={handleDeleteClick}
                    onDefaultClick={handleDefaultClick}
                    onUpdate={handleUpdateClick}
                />
            </div>

            <div className="flex-grow overflow-hidden">
                <Columns data={prepareParams(params, columnsVisibility)} />
            </div>
        </div>
    );
};

interface InfoProps {
    visible: boolean;
    data: Template;
    onClose: () => void;
    callAfterClose: (visible: boolean) => void;
}

export const Info = (props: InfoProps) => {
    const { visible, data, onClose, callAfterClose } = props;

    return (
        <Drawer
            placement="right"
            width={300}
            closable={false}
            onClose={onClose}
            afterOpenChange={callAfterClose}
            open={visible}
            getContainer={false}
            destroyOnHidden
            rootStyle={{ position: 'absolute' }}
            styles={{ body: { padding: 0 }, mask: { background: 'none' } }}
        >
            {data ? <InfoContent data={data} onClose={onClose} /> : null}
        </Drawer>
    );
};
